import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:3041';
const NOTIF_POLL_MS = 30000;

const SEV_DOT = {
  WARNING:  'bg-amber-400',
  ALARM:    'bg-orange-500',
  CRITICAL: 'bg-red-500',
};
const SEV_BADGE = {
  WARNING:  'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  ALARM:    'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  CRITICAL: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

function relTime(ts) {
  if (!ts) return '';
  const min = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
  if (min < 1) return 'tani';
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

const ALL_ROLES = ['SUPERADMIN', 'AVUK', 'KRYEINSPEKTORI', 'INSPEKTORI', 'KOMPANIA'];
const ADMIN_ROLES = ['SUPERADMIN', 'AVUK', 'KRYEINSPEKTORI'];

const APP_VERSION = process.env.REACT_APP_VERSION || '1.0.0';

const meCardClass =
  'flex items-center gap-3 w-full px-4 py-2.5 rounded-xl bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-[#334155] hover:bg-gray-50 dark:hover:bg-[#2d3a4f] text-left text-gray-800 dark:text-white transition';
const meIconClass = 'w-5 h-5 shrink-0 text-gray-600 dark:text-gray-400';
const meArrow = (
  <svg className="w-5 h-5 shrink-0 text-gray-500 dark:text-gray-400 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState('dark');
  const [showUneSheet, setShowUneSheet] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Notifications
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifAlarms, setNotifAlarms] = useState([]);
  const [notifLastSeen, setNotifLastSeen] = useState(
    () => localStorage.getItem('notif-last-seen') || new Date(0).toISOString()
  );
  const notifRef = useRef(null);

  useEffect(() => {
    const savedTheme = localStorage.getItem('dio-theme') || 'dark';
    setTheme(savedTheme);
    document.documentElement.classList.toggle('dark', savedTheme === 'dark');
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'F8') {
        e.preventDefault();
        setTheme((prev) => {
          const next = prev === 'dark' ? 'light' : 'dark';
          localStorage.setItem('dio-theme', next);
          document.documentElement.classList.toggle('dark', next === 'dark');
          return next;
        });
      }
      if (e.key === 'F6') {
        e.preventDefault();
        setLanguage(language === 'sq' ? 'en' : 'sq');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setLanguage, language]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [userMenuOpen]);

  useEffect(() => {
    const fetchNotifs = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      try {
        const r = await axios.get(`${BACKEND_URL}/api/alarms?resolved=false&limit=10`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setNotifAlarms(Array.isArray(r.data) ? r.data : []);
      } catch {}
    };
    fetchNotifs();
    const id = setInterval(fetchNotifs, NOTIF_POLL_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [notifOpen]);

  const unreadCount = notifAlarms.filter(a => new Date(a.ts) > new Date(notifLastSeen)).length;

  const toggleNotif = () => {
    setNotifOpen(o => !o);
    const now = new Date().toISOString();
    setNotifLastSeen(now);
    localStorage.setItem('notif-last-seen', now);
  };

  useEffect(() => {
    setShowUneSheet(false);
  }, [location.pathname]);

  const handleLogout = () => {
    setShowUneSheet(false);
    setUserMenuOpen(false);
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const role = user?.role;

  const passesRole = useCallback((roles) => {
    return roles.includes(role);
  }, [role]);

  // Mobile bottom tabs
  const mobileBottomTabs = useMemo(() => [
    {
      path: '/dashboard',
      labelKey: 'home',
      roles: ALL_ROLES,
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>,
    },
    {
      path: '/devices',
      labelKey: 'devices',
      roles: ALL_ROLES,
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" /></svg>,
    },
    {
      path: '/map',
      labelKey: 'navKosova',
      roles: ALL_ROLES,
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>,
    },
    {
      path: '/alarms',
      labelKey: 'alarms',
      roles: ALL_ROLES,
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>,
    },
  ], []);

  const visibleBottomTabs = mobileBottomTabs.filter(item => passesRole(item.roles));

  const uneMenuDefinitions = useMemo(() => [
    { path: '/reports', labelKey: 'reports', roles: ALL_ROLES, icon: <svg className={meIconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg> },
    { path: '/alarms?tab=checkin', labelKey: 'routineCheck', roles: ['INSPEKTORI'], icon: <svg className={meIconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg> },
    { path: '/control', labelKey: 'control', roles: ['SUPERADMIN', 'AVUK', 'KRYEINSPEKTORI'], icon: <svg className={meIconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg> },
    { path: '/companies', labelKey: 'companies', roles: ['SUPERADMIN', 'AVUK'], icon: <svg className={meIconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg> },
    { path: '/users', labelKey: 'users', roles: ADMIN_ROLES, icon: <svg className={meIconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg> },
    { path: '/regions', labelKey: 'regions', roles: ['SUPERADMIN', 'AVUK'], icon: <svg className={meIconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> },
    { path: '/settings', labelKey: 'settings', roles: ALL_ROLES, icon: <svg className={meIconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg> },
  ], []);

  const visibleUneMenuItems = uneMenuDefinitions.filter(item => passesRole(item.roles));
  const showUneTab = visibleUneMenuItems.length > 0;
  const isUneRouteActive = showUneSheet || visibleUneMenuItems.some(item => isActive(item.path));
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() || user?.username || '–';

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      <div className="hidden md:block">
        <Sidebar />
      </div>

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="z-10 bg-white shadow-sm dark:bg-gray-800">
          <div className="flex items-center justify-between px-4 py-2 md:px-4 md:py-3">
            <div className="flex items-center space-x-3">
              <img
                src={`${process.env.PUBLIC_URL || ''}/assets/images/logo-dio-iot.png`}
                alt="DIO IOT"
                className="h-7 object-contain dark:hidden md:h-8"
                onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
              />
              <img
                src={`${process.env.PUBLIC_URL || ''}/assets/images/logo-dio-iot-white.png`}
                alt="DIO IOT"
                className="hidden h-7 object-contain dark:block md:h-8"
                onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
              />
              <span className="hidden dark:hidden text-blue-600 font-bold text-lg md:text-xl">DIO IoT</span>
            </div>

            <div className="flex items-center space-x-2 md:space-x-3" ref={userMenuRef}>
              {/* Theme toggle button */}
              <button
                type="button"
                onClick={() => {
                  setTheme(prev => {
                    const next = prev === 'dark' ? 'light' : 'dark';
                    localStorage.setItem('dio-theme', next);
                    document.documentElement.classList.toggle('dark', next === 'dark');
                    return next;
                  });
                }}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition"
                title="Ndrysho temën (F8)"
              >
                {theme === 'dark' ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>

              {/* Notification bell */}
              <div className="relative" ref={notifRef}>
                <button
                  type="button"
                  onClick={toggleNotif}
                  className="relative flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition"
                  title="Njoftime"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white leading-none">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {notifOpen && (
                  <div className="absolute right-0 top-full z-50 mt-2 w-80 rounded-xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800 overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
                      <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Alarme Aktive</span>
                      <span className="text-xs text-gray-400">{notifAlarms.length} gjithsej</span>
                    </div>

                    {/* List */}
                    <div className="max-h-72 overflow-y-auto">
                      {notifAlarms.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 gap-2">
                          <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-xs text-gray-400">Asnjë alarm aktiv</p>
                        </div>
                      ) : (
                        notifAlarms.map((a, i) => {
                          const isNew = new Date(a.ts) > new Date(notifLastSeen);
                          return (
                            <button
                              key={a.id || i}
                              type="button"
                              onClick={() => { setNotifOpen(false); navigate('/alarms'); }}
                              className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition border-b border-gray-50 dark:border-gray-700/50 last:border-0 ${isNew ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}
                            >
                              <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${SEV_DOT[a.severity] || 'bg-gray-400'}`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${SEV_BADGE[a.severity] || 'bg-gray-100 text-gray-600'}`}>
                                    {a.severity}
                                  </span>
                                  <span className="text-xs font-medium text-gray-900 dark:text-white truncate">{a.sensor_name || '–'}</span>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{a.message}</p>
                                {a.company_name && (
                                  <p className="text-[10px] text-gray-400 mt-0.5 truncate">{a.company_name}</p>
                                )}
                              </div>
                              <span className="text-[10px] text-gray-400 flex-shrink-0 mt-0.5">{relTime(a.ts)}</span>
                            </button>
                          );
                        })
                      )}
                    </div>

                    {/* Footer */}
                    <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-2">
                      <button
                        type="button"
                        onClick={() => { setNotifOpen(false); navigate('/alarms'); }}
                        className="w-full text-center text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline py-0.5"
                      >
                        Shih të gjitha alarmet →
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="relative hidden md:block">
                <button
                  type="button"
                  onClick={() => setUserMenuOpen(o => !o)}
                  className="flex flex-col items-end rounded-lg px-2 py-1 text-right transition hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <p className="text-xs font-semibold text-gray-900 dark:text-white">{user?.first_name} {user?.last_name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t(user?.role || '')}</p>
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full z-50 mt-1 min-w-[120px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-700">
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full rounded px-3 py-2 text-left text-xs font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                    >
                      {t('logout')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto pb-16 md:pb-0">{children}</main>

        {/* Mobile bottom nav */}
        <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-800 md:hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="flex h-14 items-center justify-around">
            {visibleBottomTabs.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex h-full flex-1 flex-col items-center justify-center transition-all duration-200 ${
                  isActive(item.path) ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {item.icon}
                <span className="mt-0.5 text-[10px] font-medium">{t(item.labelKey)}</span>
              </Link>
            ))}

            {showUneTab && (
              <button
                type="button"
                onClick={() => setShowUneSheet(true)}
                className={`flex h-full flex-1 flex-col items-center justify-center transition-all duration-200 ${
                  isUneRouteActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" />
                </svg>
                <span className="mt-0.5 text-[10px] font-medium">{t('une')}</span>
              </button>
            )}
          </div>

          {/* "Unë" Sheet */}
          {showUneSheet && (
            <>
              <button
                type="button"
                aria-label={t('close')}
                onClick={() => setShowUneSheet(false)}
                className="fixed inset-0 z-40 bg-black/40"
                style={{ bottom: '56px' }}
              />
              <div
                className="fixed left-0 right-0 z-50 flex max-h-[85vh] flex-col rounded-t-xl bg-gray-100 shadow-xl dark:bg-[#0f172a]"
                style={{ bottom: '56px' }}
              >
                <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-gray-300 dark:bg-gray-600" />
                <div className="mx-auto w-full max-w-lg flex-1 overflow-y-auto px-4 pb-4 pt-4">
                  <div className={`${meCardClass} mb-3 cursor-default`}>
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
                      <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-gray-900 dark:text-white">{fullName}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t(user?.role || '')}</p>
                    </div>
                  </div>

                  <div className="mt-3 space-y-1">
                    {visibleUneMenuItems.map(item => (
                      <Link key={item.path} to={item.path} className={meCardClass}>
                        {item.icon}
                        <span className="flex-1 text-sm">{t(item.labelKey)}</span>
                        {meArrow}
                      </Link>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={handleLogout}
                    className="mt-5 w-full rounded-xl border border-gray-200 bg-white py-2.5 font-medium text-red-600 transition hover:bg-gray-50 dark:border-[#334155] dark:bg-[#1e293b] dark:text-red-400"
                  >
                    {t('logout')}
                  </button>
                </div>
              </div>
            </>
          )}
        </nav>
      </div>
    </div>
  );
};

export default Layout;
